/**
 * BlogPage Component
 * Blog listing and individual post pages - fetches from Shopify CMS
 */

import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Layout } from '~/components/layout/Layout';
import { generateStaticPageMetaTags, applyMetaTags } from '~/lib/seo/meta-tags';
import { getBlogArticles, getBlogArticle, type BlogArticle } from '~/lib/shopify/api';
import {
  IconArrowRight,
  IconClock,
  IconHeadset,
  IconSpinner,
  IconUser,
} from '~/components/ui/Icons';

interface BlogPageProps {
  slug?: string;
}

// Calculate read time from HTML content
function calculateReadTime(html: string): string {
  const text = html.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
}

// Format date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function BlogPage({ slug }: BlogPageProps) {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch data
  useEffect(() => {
    setLoading(true);

    if (slug) {
      // Fetch single article
      getBlogArticle(slug)
        .then((data) => {
          setArticle(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      // Fetch all articles
      getBlogArticles()
        .then((data) => {
          setArticles(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [slug]);

  // Update SEO
  useEffect(() => {
    if (slug && article) {
      const meta = generateStaticPageMetaTags('blog', article.seo?.description || article.excerpt || '');
      applyMetaTags({ ...meta, title: `${article.seo?.title || article.title} | IT Remarketing Blog` });
    } else {
      const meta = generateStaticPageMetaTags('blog', 'News, tips, and insights from the team at IT Remarketing.');
      applyMetaTags({ ...meta, title: 'Blog | IT Remarketing' });
    }
  }, [slug, article]);

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="container-wide py-20 flex items-center justify-center min-h-[60vh]">
          <IconSpinner size={40} className="text-brand-orange animate-spin" />
        </div>
      </Layout>
    );
  }

  // Individual post view
  if (slug) {
    if (!article) {
      return (
        <Layout>
          <div className="container-wide py-20 text-center">
            <h1 className="text-2xl font-display font-bold text-accent-white mb-4">
              Post Not Found
            </h1>
            <p className="text-accent-silver mb-8">
              The blog post you're looking for doesn't exist.
            </p>
            <a href="/blog" className="btn-primary">
              Back to Blog
            </a>
          </div>
        </Layout>
      );
    }

    return (
      <Layout>
        {/* Hero Header */}
        <div className="bg-gradient-to-b from-surface-coal to-surface-black border-b border-surface-slate/30">
          <div className="container-wide py-12 lg:py-20">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <a href="/" className="text-accent-silver hover:text-accent-white transition-colors">
                    Home
                  </a>
                </li>
                <li className="text-accent-silver">/</li>
                <li>
                  <a href="/blog" className="text-accent-silver hover:text-accent-white transition-colors">
                    Blog
                  </a>
                </li>
                <li className="text-accent-silver">/</li>
                <li className="text-brand-orange truncate max-w-[300px]">{article.title}</li>
              </ol>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl"
            >
              {/* Category Tag */}
              {article.tags.length > 0 && (
                <span className="inline-block px-4 py-1.5 bg-brand-orange/10 text-brand-orange text-sm font-semibold rounded-full mb-6">
                  {article.tags[0]}
                </span>
              )}

              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-accent-white mb-8 leading-[1.1]">
                {article.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-6 text-accent-silver">
                {article.authorV2?.name && (
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-surface-slate rounded-full flex items-center justify-center">
                      <IconUser size={20} className="text-accent-silver" />
                    </div>
                    <div>
                      <span className="block text-sm text-accent-white font-medium">{article.authorV2.name}</span>
                      <span className="block text-xs text-accent-silver">Author</span>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-2">
                    <IconClock size={16} className="text-brand-orange" />
                    {calculateReadTime(article.contentHtml)}
                  </span>
                  <span className="text-accent-silver/50">|</span>
                  <span>{formatDate(article.publishedAt)}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Featured Image - Full Width */}
        {article.image && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="container-wide -mt-8 mb-12"
          >
            <div className="aspect-[21/9] rounded-2xl overflow-hidden border border-surface-slate/30">
              <img
                src={article.image.url}
                alt={article.image.altText || article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        )}

        {/* Content */}
        <article className="container-wide py-12">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-8"
            >
              <div
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:font-display prose-headings:text-accent-white prose-headings:font-bold
                  prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                  prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                  prose-p:text-accent-silver prose-p:text-lg prose-p:leading-relaxed prose-p:mb-6
                  prose-li:text-accent-silver prose-li:text-lg
                  prose-a:text-brand-orange prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-accent-white prose-strong:font-semibold
                  prose-ul:my-6 prose-ul:pl-0 prose-li:my-2
                  prose-img:rounded-xl prose-img:my-8"
                dangerouslySetInnerHTML={{ __html: article.contentHtml }}
              />

              {/* Share / Back */}
              <div className="mt-16 pt-8 border-t border-surface-slate flex items-center justify-between">
                <a href="/blog" className="inline-flex items-center gap-2 text-accent-silver hover:text-brand-orange transition-colors">
                  <IconArrowRight size={16} className="rotate-180" />
                  Back to all posts
                </a>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.aside
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-4"
            >
              <div className="sticky top-24 space-y-8">
                {/* CTA Card */}
                <div className="p-6 bg-gradient-to-br from-brand-orange/10 to-transparent rounded-2xl border border-brand-orange/20">
                  <h3 className="text-lg font-display font-bold text-accent-white mb-3">
                    Ready to upgrade your audio?
                  </h3>
                  <p className="text-accent-silver text-sm mb-6">
                    Browse our collection of professional headsets from Jabra, Poly, and Axtel.
                  </p>
                  <a href="/products" className="btn-primary w-full justify-center text-sm">
                    Shop All Headsets
                    <IconArrowRight size={16} />
                  </a>
                </div>

                {/* Quick Links */}
                <div className="p-6 bg-surface-coal rounded-2xl border border-surface-slate/30">
                  <h3 className="text-sm font-semibold text-accent-white mb-4">Quick Links</h3>
                  <ul className="space-y-3">
                    <li>
                      <a href="/support" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-2">
                        <IconArrowRight size={14} />
                        Support Centre
                      </a>
                    </li>
                    <li>
                      <a href="/shipping" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-2">
                        <IconArrowRight size={14} />
                        Shipping Information
                      </a>
                    </li>
                    <li>
                      <a href="/returns" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-2">
                        <IconArrowRight size={14} />
                        Returns Policy
                      </a>
                    </li>
                    <li>
                      <a href="/contact" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-2">
                        <IconArrowRight size={14} />
                        Contact Us
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.aside>
          </div>
        </article>
      </Layout>
    );
  }

  // Blog listing view
  return (
    <Layout>
      {/* Hero */}
      <div className="bg-gradient-to-b from-surface-coal to-surface-black border-b border-surface-slate/30">
        <div className="container-wide py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <span className="inline-block px-3 py-1 bg-brand-orange/10 text-brand-orange text-sm font-medium rounded-full mb-4">
              Latest News
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-accent-white mb-6">
              Blog
            </h1>
            <p className="text-xl text-accent-silver">
              News, tips, and insights from the team at IT Remarketing.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container-wide py-12 lg:py-16">
        {/* Posts Grid */}
        {articles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-surface-coal rounded-2xl overflow-hidden border border-surface-slate/30 hover:border-brand-orange/30 transition-all duration-300"
              >
                <a href={`/blog/${post.handle}`} className="block">
                  {/* Thumbnail */}
                  <div className="aspect-video bg-surface-graphite overflow-hidden">
                    {post.image ? (
                      <img
                        src={post.image.url}
                        alt={post.image.altText || post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-surface-slate to-surface-coal">
                        <div className="w-16 h-16 bg-brand-orange/10 rounded-xl flex items-center justify-center">
                          <IconHeadset size={32} className="text-brand-orange" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {post.tags.length > 0 && (
                      <span className="inline-block px-2 py-0.5 bg-brand-orange/10 text-brand-orange text-xs font-semibold rounded mb-3">
                        {post.tags[0]}
                      </span>
                    )}
                    <h2 className="text-xl font-display font-bold text-accent-white mb-3 group-hover:text-brand-orange transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-accent-silver text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-accent-silver pt-4 border-t border-surface-slate/50">
                      <span>{formatDate(post.publishedAt)}</span>
                      <span className="w-1 h-1 bg-accent-silver/50 rounded-full" />
                      <span className="flex items-center gap-1">
                        <IconClock size={12} />
                        {calculateReadTime(post.contentHtml)}
                      </span>
                    </div>
                  </div>
                </a>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-surface-coal rounded-2xl flex items-center justify-center mx-auto mb-6">
              <IconHeadset size={48} className="text-brand-orange" />
            </div>
            <h2 className="text-2xl font-display font-bold text-accent-white mb-3">
              Coming Soon
            </h2>
            <p className="text-accent-silver mb-8 max-w-md mx-auto">
              Our blog is under construction. Check back soon for news, tips, and insights from the team.
            </p>
            <a href="/products" className="btn-primary inline-flex">
              Shop Headsets
              <IconArrowRight size={18} />
            </a>
          </div>
        )}
      </div>
    </Layout>
  );
}
